import { z } from 'zod';
import { isValidTimeZone } from '../../dates/index.js';
import { CAL_EVENT_MAX_CHECKLIST_ITEMS } from '../../events/cal-event-max-checklist-items.js';
import { CAL_EVENT_TEMPLATE_TEXT_MAX_LENGTH } from '../../events/cal-event-template-text-max-length.js';
import { hasAtMostDecimals } from '../../events/has-at-most-decimals.js';
import { TEMPLATE_NUMBER_DECIMALS } from '../../events/template-number-decimals.js';
import { TEMPLATE_NUMBER_LIMIT } from '../../events/template-number-limit.js';
import { newTagsSchema } from '../cal-tag/new-tags-schema.js';
import { localDateInput, optionalUuidString, requiredUuidString } from '../zod-helpers/index.js';
import { calEventAssigneeSchema } from './cal-event-assignee-schema.js';
import { calEventChecklistItemSchema } from './cal-event-checklist-item-schema.js';
import { calEventScheduleShape, refineCalEventSchedule } from './cal-event-schedule-schema.js';
import { calEventTagIdsSchema } from './cal-event-tag-ids-schema.js';
import { venueAddressSchema } from './venue-address-schema.js';
const templateTextValueSchema = z.object({
    templateFieldId: requiredUuidString('Template field ID is required.', 'Invalid template field ID.'),
    value: z
        .string()
        .trim()
        .max(CAL_EVENT_TEMPLATE_TEXT_MAX_LENGTH, `Value must be ${CAL_EVENT_TEMPLATE_TEXT_MAX_LENGTH} characters or fewer.`),
});
const templateNumberValueSchema = z.object({
    templateFieldId: requiredUuidString('Template field ID is required.', 'Invalid template field ID.'),
    value: z
        .number()
        .gt(-TEMPLATE_NUMBER_LIMIT, 'Number is too small.')
        .lt(TEMPLATE_NUMBER_LIMIT, 'Number is too large.')
        .refine((value) => hasAtMostDecimals(value, TEMPLATE_NUMBER_DECIMALS), `Number can have at most ${TEMPLATE_NUMBER_DECIMALS} decimals.`),
});
const templateDateValueSchema = z.object({
    templateFieldId: requiredUuidString('Template field ID is required.', 'Invalid template field ID.'),
    value: localDateInput('Invalid date value.'),
});
const templateSelectValueSchema = z.object({
    templateFieldId: requiredUuidString('Template field ID is required.', 'Invalid template field ID.'),
    value: z.string().min(1),
});
const templateEntityRefValueSchema = z
    .object({
    templateFieldId: requiredUuidString('Template field ID is required.', 'Invalid template field ID.'),
    salesOrderId: optionalUuidString('Invalid sales order ID.').optional().nullable(),
    promotionId: optionalUuidString('Invalid promotion ID.').optional().nullable(),
    sysUserId: optionalUuidString('Invalid user ID.').optional().nullable(),
    productId: optionalUuidString('Invalid product ID.').optional().nullable(),
})
    .refine((row) => [row.salesOrderId, row.promotionId, row.sysUserId, row.productId].some(Boolean), { message: 'Select a record for this field.' });
const TEMPLATE_VALUE_KEYS = [
    'templateTextValues',
    'templateNumberValues',
    'templateDateValues',
    'templateSelectValues',
    'templateEntityRefValues',
];
export const createCalEventInputSchema = z
    .object({
    brandId: requiredUuidString('Brand is required.', 'Invalid brand ID.'),
    customerId: optionalUuidString('Invalid store ID.').optional().nullable(),
    name: z
        .string()
        .trim()
        .min(1, 'Name is required.')
        .max(128, 'Name must be 128 characters or fewer.'),
    ...calEventScheduleShape,
    note: z.string().max(4096, 'Note must be 4096 characters or fewer.').optional().nullable(),
    promotionId: optionalUuidString('Invalid promotion.').optional().nullable(),
    templateVersionId: optionalUuidString('Invalid event type.').optional().nullable(),
    checkInRequired: z.boolean().optional().default(false),
    needsApproval: z.boolean().optional().default(false),
    underReviewRequired: z.boolean().optional().default(false),
    photoRequired: z.boolean().optional().default(false),
    requiresProductSummary: z.boolean().optional(),
    ownerUserId: optionalUuidString('Invalid owner.').optional().nullable(),
    timeZone: z.string().refine(isValidTimeZone, 'Invalid time zone.').optional().nullable(),
    venueAddress: venueAddressSchema.optional().nullable(),
    assignees: z
        .array(calEventAssigneeSchema)
        .max(50, 'An event can have at most 50 people.')
        .optional()
        .default([]),
    newTags: newTagsSchema,
    tagIds: calEventTagIdsSchema.optional().default([]),
    checklistItems: z
        .array(calEventChecklistItemSchema)
        .max(CAL_EVENT_MAX_CHECKLIST_ITEMS, `An event can have at most ${CAL_EVENT_MAX_CHECKLIST_ITEMS} checklist items.`)
        .optional()
        .default([]),
    templateTextValues: z
        .array(templateTextValueSchema)
        .max(50, 'An event can have at most 50 template values of each kind.')
        .optional()
        .default([]),
    templateNumberValues: z
        .array(templateNumberValueSchema)
        .max(50, 'An event can have at most 50 template values of each kind.')
        .optional()
        .default([]),
    templateDateValues: z
        .array(templateDateValueSchema)
        .max(50, 'An event can have at most 50 template values of each kind.')
        .optional()
        .default([]),
    templateSelectValues: z
        .array(templateSelectValueSchema)
        .max(50, 'An event can have at most 50 template values of each kind.')
        .optional()
        .default([]),
    templateEntityRefValues: z
        .array(templateEntityRefValueSchema)
        .max(50, 'An event can have at most 50 template values of each kind.')
        .optional()
        .default([]),
})
    .superRefine((data, ctx) => {
    refineCalEventSchedule(data, ctx);
    const userIds = data.assignees.map((assignee) => assignee.userId);
    if (new Set(userIds).size !== userIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Each person can only be added once.',
            path: ['assignees'],
        });
    }
    const seenFieldIds = new Set();
    for (const key of TEMPLATE_VALUE_KEYS) {
        data[key].forEach((row, index) => {
            if (seenFieldIds.has(row.templateFieldId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Each template field can only have one value.',
                    path: [key, index, 'templateFieldId'],
                });
            }
            seenFieldIds.add(row.templateFieldId);
        });
    }
});
//# sourceMappingURL=create-cal-event-input.js.map